/**
 * `useReorderFavorites` — replace the caller's full favorites ordering.
 *
 * Wraps `PATCH /api/wiki/favorites/reorder { ids: string[] }`. The BE
 * validates the supplied set equals the caller's current favorite set
 * (no insertions / deletions allowed in the reorder route — those go
 * through add/remove) and returns 400 `reorder_set_mismatch` if not.
 *
 * Optimistic update: reorder the cached `wikiTree.favorites` array to
 * match the supplied `ids`. On error, rollback + toast.
 *
 * Settle invalidation: `['wiki', 'tree']` so the BE-assigned
 * `sortIndex` floats wins on the next render.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { apiClient } from '@/api/client'
import type { WikiNode, WikiTreeResponse } from '@/api/types-bridge'
import { useToast } from '@/components/ToastProvider'

import { WIKI_TREE_QUERY_KEY } from './query-keys'

export interface UseReorderFavoritesInput {
  ids: readonly string[]
}

interface ReorderFavoritesContext {
  previous: WikiTreeResponse | undefined
}

/**
 * Reorder `favorites` to match `ids`. Stable for any id in `ids` that
 * isn't in the current array (it's skipped); any current favorite not in
 * `ids` is dropped from the result. Both edge cases are defensive — the
 * UI builds `ids` from the current array so a perfect permutation is the
 * happy path.
 */
function permuteFavorites(
  favorites: readonly WikiNode[],
  ids: readonly string[],
): readonly WikiNode[] {
  const byId = new Map(favorites.map((f) => [f.id, f]))
  const next: WikiNode[] = []
  for (const id of ids) {
    const hit = byId.get(id)
    if (hit) next.push(hit)
  }
  return next
}

export function useReorderFavorites() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation('wiki')

  return useMutation({
    mutationFn: async (input: UseReorderFavoritesInput): Promise<void> => {
      await apiClient<unknown>('/api/wiki/favorites/reorder', {
        method: 'PATCH',
        body: { ids: input.ids },
      })
    },

    onMutate: async (input): Promise<ReorderFavoritesContext> => {
      await queryClient.cancelQueries({ queryKey: WIKI_TREE_QUERY_KEY })
      const previous = queryClient.getQueryData<WikiTreeResponse>(WIKI_TREE_QUERY_KEY)
      if (previous) {
        queryClient.setQueryData<WikiTreeResponse>(WIKI_TREE_QUERY_KEY, {
          ...previous,
          favorites: [...permuteFavorites(previous.favorites, input.ids)],
        })
      }
      return { previous }
    },

    onError: (_error, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(WIKI_TREE_QUERY_KEY, context.previous)
      }
      toast.error(t('favorites.reorderFailedToast'))
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: WIKI_TREE_QUERY_KEY })
    },
  })
}
